import InnerHTMLRenderer from "../../../../components/InnerHTMLRenderer/InnerHTMLRenderer";
import MyAccountAccordion from "../MyAccountAccordion/MyAccountAccordion";
import * as translator from "../../../../utils/translator"
import PlusCircled from "../../../../icons/Plus-Circled.svg";
import MinusCircled from "../../../../icons/Minus-Circled.svg";
import "./MyAccountOrderDetailsSupport.css";
import { useParams } from "react-router-dom";

interface MyAccountOrderDetailsSupportProps {
    language: string;
    country: string;
    orderType?: string;
    isProbatixOrder?: boolean;
    shippingCompanyCode?: string;
    expressTier?: string | null;
    isSelfPickup?: boolean;
    isCannabisOrder?: boolean;
}

// Resolves which delivery-time FAQ copy to show based on the order's actual delivery method.
// Falls back to the generic DHL-oriented content when no scenario-specific copy applies/exists.
// Matches on the carrier code, never on the display title — the title is Ops-editable free text.
export const getDeliveryScenarioSuffix = (shippingCompanyCode?: string, expressTier?: string | null, isSelfPickup?: boolean): string | null => {
    if (isSelfPickup) return 'self_pickup';
    const company = (shippingCompanyCode || '').toLowerCase();
    if (company === 'urbify') {
        if (expressTier === 'same_day') return 'urbify_same_day';
        if (expressTier === 'next_day') return 'urbify_next_day';
    }
    if (company === 'uber') return 'uber';
    return null;
};

const MyAccountOrderDetailsSupport: React.FC<MyAccountOrderDetailsSupportProps> = ({ language, country, orderType, isProbatixOrder, shippingCompanyCode, expressTier, isSelfPickup, isCannabisOrder }) => {
    const { order_hash_id } = useParams();

    const isTestKit = isProbatixOrder || orderType === 'test_kit';
    const isRxSelfPickup = !!isSelfPickup && !isCannabisOrder;
    const translationPrefix = isTestKit ? 'MyOrders/details/testKitSupport' : 'MyOrders/details/support';

    const header = translator.translate(language, "my_account", `${translationPrefix}/support_header`);

    const deliveryScenarioSuffix = getDeliveryScenarioSuffix(shippingCompanyCode, expressTier, isSelfPickup);
    const deliveryTimeContent = (deliveryScenarioSuffix &&
        translator.translate(language, "my_account", `${translationPrefix}/delivery_time_content_${deliveryScenarioSuffix}`))
        || translator.translate(language, "my_account", `${translationPrefix}/delivery_time_content`);

    if (isProbatixOrder) {
        const supportBtnText = translator.translate(language, "my_account", "MyOrders/details/testKitSupport/ask_support");
        return (
            <div className="support-section">
                <InnerHTMLRenderer htmlContent={translator.translate(language, "my_account", "MyOrders/details/testKitSupport/support_header")} className='support-header'/>
                <a href={`/${country}/contact?order=${order_hash_id}`} className="support-contact-btn">
                    {supportBtnText}
                </a>
            </div>
        );
    }

    const rxSelfPickupTranslationPrefix = 'MyOrders/details/rxSelfPickupSupport';
    const accordionItems = isTestKit ? [
        {
            title: translator.translate(language, "my_account", `${translationPrefix}/how_it_works_title`),
            content: translator.translate(language, "my_account", `${translationPrefix}/how_it_works_content`),
        },
        {
            title: translator.translate(language, "my_account", `${translationPrefix}/results_time_title`),
            content: translator.translate(language, "my_account", `${translationPrefix}/results_time_content`),
        },
        {
            title: translator.translate(language, "my_account", `${translationPrefix}/insufficient_sample_title`),
            content: translator.translate(language, "my_account", `${translationPrefix}/insufficient_sample_content`),
        },
        {
            title: translator.translate(language, "my_account", `${translationPrefix}/how_to_collect_title`),
            content: translator.translate(language, "my_account", `${translationPrefix}/how_to_collect_content`),
        },
        {
            title: translator.translate(language, "my_account", `${translationPrefix}/cancel_order_title`),
            content: translator.translate(language, "my_account", `${translationPrefix}/cancel_order_content`),
        },
        {
            title: translator.translate(language, "my_account", `${translationPrefix}/privacy_title`),
            content: translator.translate(language, "my_account", `${translationPrefix}/privacy_content`),
            button: { placeholder: translator.translate(language, "my_account", `${translationPrefix}/ask_support`), ref: `/${country}/contact`, searchParams: `order=${order_hash_id}` }
        }
    ] : isRxSelfPickup ? [
        {
            title: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/pickup_when_title`),
            content: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/pickup_when_content`),
        },
        {
            title: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/ready_notification_title`),
            content: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/ready_notification_content`),
        },
        {
            title: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/change_pharmacy_title`),
            content: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/change_pharmacy_content`),
        },
        {
            title: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/what_to_bring_title`),
            content: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/what_to_bring_content`),
        },
        {
            title: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/payment_title`),
            content: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/payment_content`),
        },
        {
            title: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/cancel_title`),
            content: translator.translate(language, "my_account", `${rxSelfPickupTranslationPrefix}/cancel_content`),
            button: { placeholder: translator.translate(language, "my_account", `MyOrders/details/support/ask_support`), ref: `/${country}/contact`, searchParams: `order=${order_hash_id}` }
        },
    ] : [
        {
            title: translator.translate(language, "my_account", `MyOrders/details/support/delivery_time_title`),
            content: deliveryTimeContent,
        },
        {
            title: translator.translate(language, "my_account", `MyOrders/details/support/medication_unavailable_title`),
            content: translator.translate(language, "my_account", `MyOrders/details/support/medication_unavailable_content`),
        },
        {
            title: translator.translate(language, "my_account", `MyOrders/details/support/order_cancel_title`),
            content: translator.translate(language, "my_account", `MyOrders/details/support/order_cancel_content`),
            button: { placeholder:translator.translate(language, "my_account", `MyOrders/details/support/ask_support`), ref:`/${country}/contact`, searchParams:`order=${order_hash_id}` }
        }
    ]

    return (
        <div className="support-section">
            <InnerHTMLRenderer htmlContent={header} className='support-header'/>
            <MyAccountAccordion
                items={accordionItems}
                accordionClass="support-accordion-container"
                itemClass="support-accordion-item"
                titleClass="support-accordion-title"
                contentClass="support-accordion-content"
                icons={{closeIcon: MinusCircled, openIcon: PlusCircled}}
                />
        </div>
    )
}

export default MyAccountOrderDetailsSupport;